import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraDashboardPage } from '@mono/ui-components/admirals';
import { SpecraBadge, SpecraButton, SpecraLink, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraInlineBanner, SpecraPageHeader, SpecraSectionHeader } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { format } from 'date-fns';
import { Link, useParams } from 'react-router-dom';
import { renderRouterLink } from '../../../lib/renderRouterLink';
import { RfiHomeRail } from './RfiHomeRail';
import { RfiQuestionsTable } from './RfiQuestionsTable';
import { RfiReadinessList } from './RfiReadinessList';
import { useRfiRecord } from './RfiRecordContext';
import { RfiSignalsList } from './RfiSignalsList';
import { homeMetrics, rfiStatusBadge } from './rfiHomeModel';
import { useRfiWorkspaceHome } from './useRfiWorkspaceHome';
const T = Testids.rfiWorkspace;
/**
 * RFI Home (WRT-06, `RfiHome.png`): header, four KPIs, the questions as the
 * agency wrote them, signals posted to the pursuit thread, and the record
 * rail (notice, submission, people and organizations, linked pursuits, team).
 * Everything except the team list comes from one `GetRfiWorkspaceHome` call.
 */ export function RfiHomePage() {
    const { id = '' } = useParams();
    const record = useRfiRecord();
    const rfiId = record?.pangaeaId ?? '';
    const { data: home, isLoading, error } = useRfiWorkspaceHome(rfiId);
    const base = `/rfi-response/${id}`;
    if (error) {
        return /*#__PURE__*/ _jsxs(SpecraInlineBanner, {
            className: "m-[var(--space-6)]",
            tone: "danger",
            children: [
                "The RFI home could not load. ",
                error.message
            ]
        });
    }
    if (isLoading || !home) {
        return /*#__PURE__*/ _jsx(SpecraEmptyState, {
            className: "m-[var(--space-6)]",
            "data-testid": T.home,
            description: "Loading the RFI home…",
            framed: true,
            title: record?.title ?? 'RFI'
        });
    }
    const k = home.kpis;
    const status = rfiStatusBadge(home.status);
    const due = home.responseDue?.toDate();
    const metrics = homeMetrics({
        answersFinal: k?.answersFinal ?? 0,
        answersTotal: k?.answersTotal ?? 0,
        inReview: k?.inReview ?? 0,
        drafting: k?.drafting ?? 0,
        noOwner: k?.noOwner ?? 0,
        noOwnerQuestionNumbers: k?.noOwnerQuestionNumbers ?? [],
        pagesEstimate: k?.pagesEstimate ?? 0,
        pageLimit: k?.pageLimit ?? 0,
        pagesBasis: k?.pagesBasis ?? 'estimate',
        daysLeft: k?.daysLeft ?? -1,
        nextInternalDue: k?.nextInternalDue?.toDate()
    });
    const pursuits = home.linkedPursuits.map((p)=>({
            id: p.captureProjectId,
            title: p.title
        }));
    return /*#__PURE__*/ _jsx(SpecraDashboardPage, {
        "data-testid": T.home,
        header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
            actions: /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap gap-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        asChild: true,
                        "data-testid": T.previewPackage,
                        size: "sm",
                        variant: "secondary",
                        children: /*#__PURE__*/ _jsx(Link, {
                            to: `${base}/legacy#package`,
                            children: "Preview package"
                        })
                    }),
                    home.canRecordSubmission ? /*#__PURE__*/ _jsx(SpecraButton, {
                        asChild: true,
                        "data-testid": T.recordSubmission,
                        size: "sm",
                        children: /*#__PURE__*/ _jsx(Link, {
                            to: `${base}/legacy#submission`,
                            children: "Record submission"
                        })
                    }) : null
                ]
            }),
            meta: /*#__PURE__*/ _jsxs("span", {
                className: "inline-flex flex-wrap items-center gap-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: status.variant,
                        children: status.label
                    }),
                    due ? /*#__PURE__*/ _jsxs(SpecraBadge, {
                        variant: (k?.daysLeft ?? 99) <= 2 && (k?.daysLeft ?? -1) >= 0 ? 'danger' : 'warning',
                        children: [
                            "Due ",
                            format(due, 'EEE d MMM, h:mm a')
                        ]
                    }) : null,
                    /*#__PURE__*/ _jsx(SpecraText, {
                        size: "meta",
                        tone: "muted",
                        children: home.subtitleNote
                    })
                ]
            }),
            title: home.title,
            titleSize: "home"
        }),
        metrics: {
            metrics,
            getItemTestId: ()=>T.homeKpis
        },
        padding: "page",
        rail: /*#__PURE__*/ _jsx(RfiHomeRail, {
            home: home,
            rfiId: rfiId,
            zouProjectId: record?.zouProjectId ?? ''
        }),
        railVariant: "record",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-col gap-[var(--space-6)]",
            children: [
                /*#__PURE__*/ _jsxs("section", {
                    className: "flex flex-col gap-[var(--space-2)]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            action: /*#__PURE__*/ _jsx(SpecraLink, {
                                href: `${base}/questions`,
                                renderLink: renderRouterLink,
                                children: "Open questions →"
                            }),
                            title: `Questions, as ${home.agencyShort === 'The agency' ? 'the agency' : home.agencyShort} wrote them`,
                            variant: "group"
                        }),
                        /*#__PURE__*/ _jsx(RfiQuestionsTable, {
                            base: base,
                            rfiId: rfiId,
                            rows: home.questions
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("section", {
                    className: "flex flex-col gap-[var(--space-2)]",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            title: "What this RFI tells us · posted to the pursuit thread",
                            variant: "group"
                        }),
                        /*#__PURE__*/ _jsx(RfiSignalsList, {
                            canEdit: home.canEdit,
                            entities: home.entities,
                            linkedPursuits: pursuits,
                            rfiResponseProjectId: rfiId,
                            signals: home.signals
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(RfiReadinessList, {
                    rfiResponseProjectId: rfiId,
                    routeId: id
                })
            ]
        })
    });
}
